/**
 * LivePreview2026 - Enhanced Live Preview with Validation Feedback
 * 
 * PREMIUM 2026 UX:
 * - Micro-animations on value changes
 * - Intelligent validation feedback
 * - Smooth transitions
 * - Visual hierarchy
 */

import { formatCurrency } from "../../../utils/formatCurrency";
import { CheckCircle, AlertCircle, Eye, Send, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

export default function LivePreview2026({ summary, validation, onFullPreview, canSend, isEditable, type = "invoice" }) {
  const [changedFields, setChangedFields] = useState(new Set());
  const [prevSummary, setPrevSummary] = useState(summary);

  // Detect changes and highlight briefly
  useEffect(() => {
    if (!prevSummary) {
      setPrevSummary(summary);
      return;
    }

    const changed = new Set();
    if (summary.total !== prevSummary.total) changed.add('total');
    if (summary.subtotal !== prevSummary.subtotal) changed.add('subtotal');
    if (summary.taxTotal !== prevSummary.taxTotal) changed.add('tax');
    if (summary.items?.length !== prevSummary.items?.length) changed.add('items');

    if (changed.size > 0) {
      setChangedFields(changed);
      const timer = setTimeout(() => setChangedFields(new Set()), 800);
      setPrevSummary(summary);
      return () => clearTimeout(timer);
    }
  }, [summary, prevSummary]);

  if (!summary) {
    return (
      <aside className="h-full flex flex-col bg-white border border-slate-200/60 rounded-xl shadow-sm">
        <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
          <div className="animate-pulse">Loading preview...</div>
        </div>
      </aside>
    );
  }

  const items = summary.items || [];
  const hasItems = items.length > 0;
  const hasClient = summary.hasClient;

  // Readiness check
  const readinessChecks = [
    { label: 'Client added', completed: hasClient, required: true },
    { label: 'Items added', completed: hasItems, required: true },
    { label: 'GST configured', completed: hasItems && items.some(i => i.metadata), required: false },
  ];

  const requiredComplete = readinessChecks.filter(c => c.required).every(c => c.completed);

  return (
    <aside className="h-full flex flex-col overflow-hidden">
      
      {/* Header */}
      <div className="px-4 py-3 border-b-2 border-slate-300 bg-gradient-to-b from-slate-50/80 via-slate-50/40 to-transparent">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className={`tracking-tight flex items-center gap-1.5 ${
              type === "quotation" ? "text-sm font-extrabold text-slate-900" : "text-xs font-bold text-slate-900"
            }`}>
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {type === "quotation" ? "Quotation Preview" : "Live Preview"}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
              <Sparkles size={9} className="text-indigo-400" />
              Updates in real-time
            </div>
          </div>
          <button 
            onClick={onFullPreview}
            className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-2 py-1 rounded-md transition-all duration-150 flex items-center gap-1"
          >
            <Eye size={11} />
            Full
          </button>
        </div>

        {/* Readiness Checklist - ENHANCED */}
        {isEditable && (
          <div className={`rounded-lg p-2.5 transition-all duration-300 ${
            requiredComplete 
              ? 'bg-gradient-to-br from-emerald-50 via-emerald-50/80 to-emerald-100/50 border border-emerald-200/60 shadow-sm' 
              : 'bg-gradient-to-br from-amber-50 via-amber-50/80 to-amber-100/50 border border-amber-200/60'
          }`}>
            <div className="flex items-start gap-2 mb-2">
              {requiredComplete ? (
                <CheckCircle size={14} className="text-emerald-600 flex-shrink-0 mt-0.5 animate-in fade-in duration-300" />
              ) : (
                <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`text-[11px] font-bold ${
                  requiredComplete ? 'text-emerald-900' : 'text-amber-900'
                }`}>
                  {requiredComplete ? '✓ Ready to send' : 'Almost ready to send'}
                </div>
              </div>
            </div>
            
            {/* Checklist Items */}
            <div className="space-y-1 ml-6">
              {readinessChecks.map((check, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[10px]">
                  {check.completed ? (
                    <CheckCircle size={10} className="text-emerald-600" />
                  ) : (
                    <div className="w-2.5 h-2.5 rounded-full border-2 border-amber-400" />
                  )}
                  <span className={check.completed ? 'text-emerald-700' : 'text-amber-700'}>
                    {check.label}
                  </span>
                  {!check.required && (
                    <span className="text-[9px] text-slate-400">(optional)</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gradient-to-b from-transparent to-slate-50/30">
        
        {/* Invoice/Quotation Meta */}
        <div className="space-y-1.5">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-bold text-slate-900 tracking-tight">
              {type === "quotation" ? summary.quoteNumber : summary.invoiceNumber}
            </span>
            <StatusBadge status={summary.status} paymentStatus={summary.paymentStatus} type={type} />
          </div>
          <div className="text-[11px] text-slate-600">
            {(type === "quotation" ? summary.quoteDate : summary.issueDate) ? (
              new Date(type === "quotation" ? summary.quoteDate : summary.issueDate).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            ) : (
              <span className="text-slate-400 italic">Date not set</span>
            )}
          </div>
        </div>

        {/* Bill To */}
        <div className="space-y-1.5">
          <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
            Bill To
          </div>
          {summary.hasClient ? (
            <div className="space-y-0.5">
              <div className="text-xs font-semibold text-slate-900">
                {summary.billTo.name}
              </div>
              {summary.billTo.location && (
                <div className="text-[11px] text-slate-600">
                  {summary.billTo.location}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-slate-400 italic flex items-center gap-1">
              <AlertCircle size={10} />
              Client not selected
            </div>
          )}
        </div>

        {/* Line Items */}
        <div className="space-y-1.5">
          <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
            Items ({items.length})
          </div>
          {!hasItems ? (
            <div className="py-6 text-center">
              <div className="text-[11px] text-slate-400 italic flex items-center justify-center gap-1">
                <AlertCircle size={10} />
                No items added yet
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {items.map((item, i) => (
                <div
                  key={i}
                  className={`group p-2.5 rounded-lg bg-white border border-slate-100 space-y-1 hover:border-indigo-200 hover:shadow-sm transition-all duration-200 cursor-pointer ${
                    changedFields.has('items') ? 'animate-in fade-in slide-in-from-top-2 duration-300 shadow-sm shadow-indigo-100/50' : ''
                  }`}
                >
                  <div className="text-xs font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.name}</div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] text-slate-600">
                      {item.quantity} × {formatCurrency(item.rate)}
                    </span>
                    {item.metadata && (
                      <span className="text-[9px] text-slate-400 uppercase tracking-wide font-medium">
                        {item.metadata}
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-bold text-slate-900 tabular-nums">
                    {formatCurrency(item.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Totals - ENHANCED HIERARCHY */}
        <div className="space-y-2 pt-3 border-t-2 border-slate-200">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-600">Subtotal</span>
            <span className={`text-xs font-semibold text-slate-900 tabular-nums transition-all duration-300 ${
              changedFields.has('subtotal') ? 'text-indigo-600 scale-105 drop-shadow-sm' : ''
            }`}>
              {formatCurrency(summary.subtotal)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-600">Tax</span>
            <span className={`text-xs font-semibold text-slate-900 tabular-nums transition-all duration-300 ${
              changedFields.has('tax') ? 'text-indigo-600 scale-105 drop-shadow-sm' : ''
            }`}>
              {formatCurrency(summary.taxTotal)}
            </span>
          </div>
          {summary.hasAdjustments && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-slate-600">Adjustments</span>
              <span className="text-xs font-semibold text-slate-900 tabular-nums">
                {summary.adjustments > 0 ? '+' : ''}{formatCurrency(Math.abs(summary.adjustments))}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between pt-3 mt-2 border-t-2 border-slate-300">
            <span className="text-sm font-bold text-slate-900">Total</span>
            <span className={`text-2xl font-bold text-slate-900 tracking-tight tabular-nums transition-all duration-300 ${
              changedFields.has('total') ? 'text-indigo-600 scale-110 drop-shadow-sm' : ''
            }`}>
              {formatCurrency(summary.total)}
            </span>
          </div>
          {summary.hasPayments && (
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-200">
              <span className="text-[11px] font-semibold text-slate-600">Balance Due</span>
              <span className="text-sm font-bold text-slate-900 tabular-nums">
                {formatCurrency(summary.balanceDue)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer - Quick Action Hint */}
      {isEditable && canSend && (
        <div className="px-4 py-2.5 border-t border-slate-100/80 bg-gradient-to-t from-slate-50/50 to-transparent">
          <div className="text-[10px] text-slate-600 text-center flex items-center justify-center gap-1.5">
            <Send size={10} className="text-indigo-600" />
            <span>Press <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[9px] font-mono font-semibold">⌘ Enter</kbd> to send</span>
          </div>
        </div>
      )}
    </aside>
  );
}

/* ───────── Status Badge ───────── */
function StatusBadge({ status, paymentStatus, type = "invoice" }) {
  if (status === "draft") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
        Draft
      </span>
    );
  }

  if (status === "sent") {
    if (type === "invoice") {
      if (paymentStatus === "paid") {
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Paid
          </span>
        );
      }
      if (paymentStatus === "partially_paid") {
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
            Partial
          </span>
        );
      }
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
        Sent
      </span>
    );
  }

  // Quotation-specific statuses
  if (type === "quotation") {
    if (status === "accepted") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Accepted
        </span>
      );
    }
    if (status === "converted") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
          Converted
        </span>
      );
    }
    if (status === "expired") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          Expired
        </span>
      );
    }
  }

  return null;
}
